import { useMemo } from 'react'

import { range } from 'lodash'

import { Pagination } from './Pagination'

import { useFilters } from '@/context/filterContext'
import { useListViewInfo } from '@/context/listViewContext'
import { useQueryParams } from '@/context/queryParamsContext'
import {
  Params,
  CustomPaginationQueryKeys,
  DEFAULT_PAGINATION_KEYS,
} from '@/utils/params'

import { PaginationAggregation } from 'types/api.graphql'

type PaginationInfo = {
  currentPage: number
  totalPages: number
  totalCount: number
}

const getPaginationInfo = (
  aggregation: PaginationAggregation
): PaginationInfo => {
  if (aggregation) {
    return {
      currentPage: (aggregation.offset + aggregation.limit) / aggregation.limit,
      totalCount: aggregation.totalCount,
      totalPages: aggregation.totalPages,
    }
  }
  return null
}

type Props = {
  useQuery?: boolean
  customQueryKeys?: CustomPaginationQueryKeys
  pageSizeOptions?: number[]
}

export const CorePageListViewPagination: React.FC<Props> = ({
  useQuery,
  customQueryKeys,
  pageSizeOptions,
}) => {
  const { updateParams } = useQueryParams<Params.Pagination>()

  const { routeFilters, updateFilters } = useFilters()
  const { resourceSingular, aggregation } = useListViewInfo()

  const info = useMemo(() => getPaginationInfo(aggregation), [aggregation])

  if (!info) return null

  const handlePageClick = (pageNumber: number) => {
    if (useQuery) {
      const key = customQueryKeys?.page ?? DEFAULT_PAGINATION_KEYS.page
      updateParams({ [key]: pageNumber })
    } else {
      updateFilters({
        ...routeFilters,
        pagination: {
          ...routeFilters?.pagination,
          current: pageNumber,
          pageSize: aggregation.limit,
          total: aggregation.totalCount,
        },
      })
    }
  }

  const handlePageSizeClick = (pageSize: number) => {
    if (useQuery) {
      const key = customQueryKeys?.pagesize ?? DEFAULT_PAGINATION_KEYS.pageSize
      updateParams({ [key]: pageSize })
    } else {
      updateFilters({
        ...routeFilters,
        pagination: {
          ...routeFilters?.pagination,
          current: 1,
          pageSize,
          total: aggregation.totalCount,
        },
      })
    }
  }

  const truncateStart = info.currentPage > 3
  const truncateEnd = info.totalPages + 1 - info.currentPage > 3

  const chunks = []
  if (!truncateStart && !truncateEnd) {
    chunks.push(range(1, info.totalPages + 1))
  } else if (truncateStart && !truncateEnd) {
    chunks.push([1], range(info.totalPages - 3, info.totalPages + 1))
  } else if (!truncateStart && truncateEnd) {
    chunks.push(range(1, 5), [info.totalPages])
  } else if (truncateStart && truncateEnd) {
    chunks.push([1], range(info.currentPage - 1, info.currentPage + 2), [
      info.totalPages,
    ])
  }

  return (
    <Pagination
      currentPage={info.currentPage}
      totalPages={info.totalPages}
      pageSize={aggregation.limit}
      onPageClick={handlePageClick}
      onPageSizeClick={handlePageSizeClick}
      resourceSingular={resourceSingular}
      pageSizeOptions={pageSizeOptions}
    />
  )
}
